import React, {useEffect, useMemo, useRef, useState} from "react";

type SmartImageProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  sources: string[];
  placeholder?: React.ReactNode;
  fadeInMs?: number;
  lazy?: boolean;
  rootMargin?: string;
};

const loadImage = (src: string) =>
  new Promise<string>((resolve, reject) => {
    if (!src) return reject(new Error("Empty src"));
    const img = new Image();
    img.onload = () => resolve(src);
    img.onerror = () => reject(new Error("Image load error"));
    img.decoding = "async";
    img.loading = "eager";
    img.src = src;
  });

export function SmartImage({
                             sources,
                             placeholder,
                             fadeInMs = 180,
                             alt,
                             lazy = true,
                             rootMargin = "200px",
                             ...imgProps
                           }: SmartImageProps) {
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [canStartLoading, setCanStartLoading] = useState(!lazy);
  const holderRef = useRef<HTMLSpanElement | null>(null);

  const queue = useMemo(
    () => sources.filter(Boolean).map((s) => s.trim()).filter(Boolean),
    [sources]
  );

  useEffect(() => {
    if (!lazy) return;

    if ("IntersectionObserver" in window && holderRef.current) {
      const ob = new IntersectionObserver(
        (entries) => {
          const isVisible = entries.some((e) => e.isIntersecting);
          if (isVisible) {
            setCanStartLoading(true);
            ob.disconnect();
          }
        },
        { root: null, rootMargin, threshold: 0 }
      );
      ob.observe(holderRef.current);
      return () => ob.disconnect();
    } else {
      setCanStartLoading(true);
    }
  }, [lazy, rootMargin]);

  useEffect(() => {
    if (!canStartLoading) return;

    let cancelled = false;
    setLoadedSrc(null);
    setFailed(false);

    (async () => {
      for (const src of queue) {
        try {
          const ok = await loadImage(src);
          if (!cancelled) {
            setLoadedSrc(ok);
            return;
          }
        } catch {}
      }
      if (!cancelled) setFailed(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [canStartLoading, queue.join("|")]);

  if (!canStartLoading) {
    return (
      <span ref={holderRef}>
        {placeholder ?? (
          <div
            aria-hidden
            style={{
              width: imgProps.width,
              height: imgProps.height,
              display: "inline-block",
              background:
                "linear-gradient(90deg,#eee 25%,#f5f5f5 37%,#eee 63%)",
              backgroundSize: "400% 100%",
              animation: "smartImgShimmer 1.2s infinite",
              borderRadius: 8,
            }}
          />
        )}
        <style>
          {`@keyframes smartImgShimmer {
            0% { background-position: 100% 0 }
            100% { background-position: 0 0 }
          }`}
        </style>
      </span>
    );
  }

  if (failed && !loadedSrc) {
    return (
      <div
        aria-label={alt}
        style={{
          width: imgProps.width,
          height: imgProps.height,
          display: "inline-block",
          background: "#f2f2f2",
          borderRadius: 8,
        }}
      />
    );
  }

  if (!loadedSrc) {
    return (
      <>
        {placeholder ?? (
          <div
            aria-hidden
            style={{
              width: imgProps.width,
              height: imgProps.height,
              display: "inline-block",
              background:
                "linear-gradient(90deg,#eee 25%,#f5f5f5 37%,#eee 63%)",
              backgroundSize: "400% 100%",
              animation: "smartImgShimmer 1.2s infinite",
              borderRadius: 8,
            }}
          />
        )}
        <style>
          {`@keyframes smartImgShimmer {
            0% { background-position: 100% 0 }
            100% { background-position: 0 0 }
          }`}
        </style>
      </>
    );
  }

  return (
    <img
      {...imgProps}
      alt={alt}
      src={loadedSrc}
      loading={imgProps.loading ?? "lazy"}
      style={{
        ...(imgProps.style ?? {}),
        opacity: 1,
        transition: `opacity ${fadeInMs}ms ease`,
      }}
      onError={() => {
        const idx = queue.indexOf(loadedSrc);
        const next = queue[idx + 1];
        if (next) {
          setLoadedSrc(null);
          (async () => {
            try {
              const ok = await loadImage(next);
              setLoadedSrc(ok);
            } catch {
              setFailed(true);
            }
          })();
        } else {
          setFailed(true);
        }
      }}
    />
  );
}